const MAX_ROWS = 1048576;
const MAX_COLUMNS = 16384;
let encoderRef;
const encode = (value) => (encoderRef ||= new TextEncoder()).encode(value);

const crcTable = Array.from({ length: 256 }, (_, value) => {
  let crc = value;
  for (let bit = 0; bit < 8; bit += 1) crc = (crc & 1) ? (0xedb88320 ^ (crc >>> 1)) : (crc >>> 1);
  return crc >>> 0;
});

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    crc = crcTable[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const write16 = (view, offset, value) => view.setUint16(offset, value, true);
const write32 = (view, offset, value) => view.setUint32(offset, value >>> 0, true);

const concat = (parts) => {
  const length = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(length);
  let offset = 0;
  parts.forEach((part) => { output.set(part, offset); offset += part.length; });
  return output;
};

const deflateRaw = async (data) => {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([data]).stream().pipeThrough(new CompressionStream('deflate-raw'));
    const reader = stream.getReader();
    const chunks = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    return concat(chunks);
  } catch (_error) {
    return null;
  }
};

const prepareZipEntries = async (entries) => {
  const prepared = [];
  for (let index = 0; index < entries.length; index += 1) {
    const { name, content } = entries[index];
    const data = typeof content === 'string' ? encode(content) : content;
    const deflated = await deflateRaw(data);
    prepared.push({
      name,
      data,
      compressed: deflated || data,
      method: deflated ? 8 : 0,
      crc: crc32(data),
    });
  }
  return prepared;
};

const zipArchive = (entries) => {
  const localParts = [];
  const centralParts = [];
  let localOffset = 0;

  entries.forEach(({ name, data, compressed, method, crc }) => {
    const nameBytes = encode(name);
    const local = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(local.buffer);
    write32(localView, 0, 0x04034b50);
    write16(localView, 4, 20);
    write16(localView, 6, 0x0800);
    write16(localView, 8, method);
    write32(localView, 14, crc);
    write32(localView, 18, compressed.length);
    write32(localView, 22, data.length);
    write16(localView, 26, nameBytes.length);
    local.set(nameBytes, 30);
    localParts.push(local, compressed);

    const central = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(central.buffer);
    write32(centralView, 0, 0x02014b50);
    write16(centralView, 4, 20);
    write16(centralView, 6, 20);
    write16(centralView, 8, 0x0800);
    write16(centralView, 10, method);
    write32(centralView, 16, crc);
    write32(centralView, 20, compressed.length);
    write32(centralView, 24, data.length);
    write16(centralView, 28, nameBytes.length);
    write32(centralView, 42, localOffset);
    central.set(nameBytes, 46);
    centralParts.push(central);
    localOffset += local.length + compressed.length;
  });

  const centralDirectory = concat(centralParts);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  write32(endView, 0, 0x06054b50);
  write16(endView, 8, entries.length);
  write16(endView, 10, entries.length);
  write32(endView, 12, centralDirectory.length);
  write32(endView, 16, localOffset);
  return concat([...localParts, centralDirectory, end]);
};

export const escapeXml = (value) => Array.from(String(value ?? ''))
  .filter((character) => {
    const code = character.charCodeAt(0);
    return code >= 32 || code === 9 || code === 10 || code === 13;
  })
  .join('')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

export const sanitizeSheetName = (value, usedNames = new Set()) => {
  const fallback = 'Sheet';
  let base = String(value || fallback)
    .replace(/[:\\/?*[\]]/g, '-')
    .replace(/^'+|'+$/g, '')
    .trim() || fallback;
  if (base.toLowerCase() === 'history') base = `${base}-`;
  base = base.slice(0, 31);
  let name = base;
  let suffix = 2;
  const lowerNames = new Set(Array.from(usedNames, (item) => String(item).toLowerCase()));
  while (lowerNames.has(name.toLowerCase())) {
    const tail = ` (${suffix})`;
    name = `${base.slice(0, 31 - tail.length)}${tail}`;
    suffix += 1;
  }
  usedNames.add(name);
  return name;
};

export const sanitizeFileName = (value) => String(value || 'SCAPP.xlsx')
  .replace(/[\\/:*?"<>|]/g, '-')
  .replace(/\s+/g, '_');

const columnName = (index) => {
  let value = index + 1;
  let result = '';
  while (value > 0) {
    value -= 1;
    result = String.fromCharCode(65 + (value % 26)) + result;
    value = Math.floor(value / 26);
  }
  return result;
};

export const excelSerial = (date) => (
  date.getTime() - date.getTimezoneOffset() * 60000
) / 86400000 + 25569;

const freezePaneXml = (cell) => {
  if (!cell) return '';
  const match = String(cell).toUpperCase().match(/^([A-Z]+)(\d+)$/);
  if (!match) return '';
  const xSplit = Array.from(match[1]).reduce((value, letter) => value * 26 + letter.charCodeAt(0) - 64, 0) - 1;
  const ySplit = Math.max(0, Number(match[2]) - 1);
  const activePane = xSplit && ySplit ? 'bottomRight' : xSplit ? 'topRight' : 'bottomLeft';
  return `<pane${xSplit ? ` xSplit="${xSplit}"` : ''}${ySplit ? ` ySplit="${ySplit}"` : ''} topLeftCell="${escapeXml(cell)}" activePane="${activePane}" state="frozen"/>`;
};

const cellXml = (value, ref, defaultStyle) => {
  if (value == null || (typeof value === 'number' && !Number.isFinite(value))) return '';
  const wrapped = value && typeof value === 'object' && !(value instanceof Date)
    && Object.prototype.hasOwnProperty.call(value, 'v');
  const raw = wrapped ? value.v : value;
  const style = wrapped ? value.s : defaultStyle;
  if (raw == null || (typeof raw === 'number' && !Number.isFinite(raw))) return '';
  const styleAttr = style == null ? '' : ` s="${Number(style)}"`;
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) return '';
    return `<c r="${ref}"${styleAttr || ' s="6"'}><v>${excelSerial(raw)}</v></c>`;
  }
  if (typeof raw === 'number') return `<c r="${ref}"${styleAttr}><v>${raw}</v></c>`;
  if (typeof raw === 'boolean') return `<c r="${ref}" t="b"${styleAttr}><v>${raw ? 1 : 0}</v></c>`;
  return `<c r="${ref}" t="inlineStr"${styleAttr}><is><t xml:space="preserve">${escapeXml(String(raw).slice(0, 32767))}</t></is></c>`;
};

const sheetXml = ({ columns, rows, options = {} }) => {
  const lastColumn = columnName(Math.max(0, columns.length - 1));
  const header = columns.map((column, index) => cellXml(column.header, `${columnName(index)}1`, 1)).join('');
  const body = rows.map((row, rowIndex) => {
    const number = rowIndex + 2;
    const cells = columns.map((column, columnIndex) => (
      cellXml(row?.[column.key], `${columnName(columnIndex)}${number}`, row?.$style ?? column.style)
    )).join('');
    return `<row r="${number}">${cells}</row>`;
  }).join('');
  const widths = columns.map((column, index) => (
    `<col min="${index + 1}" max="${index + 1}" width="${Math.max(4, Number(column.width) || 14)}" customWidth="1"/>`
  )).join('');
  const freeze = options.freeze || 'A2';
  const pane = freezePaneXml(freeze);
  const filter = options.autoFilter === false || !rows.length ? '' : `<autoFilter ref="A1:${lastColumn}${rows.length + 1}"/>`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
    + `<sheetViews><sheetView workbookViewId="0">${pane}</sheetView></sheetViews>`
    + `<cols>${widths}</cols><sheetData><row r="1">${header}</row>${body}</sheetData>${filter}</worksheet>`;
};

const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="5"><numFmt numFmtId="164" formatCode="0.00"/><numFmt numFmtId="165" formatCode="0.0&quot;%&quot;"/><numFmt numFmtId="166" formatCode="dd/mm/yyyy"/><numFmt numFmtId="167" formatCode="dd/mm/yyyy hh:mm:ss"/><numFmt numFmtId="168" formatCode="0&quot;s&quot;"/></numFmts>
  <fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
  <fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFD9A184"/><bgColor indexed="64"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFF2DFD4"/><bgColor indexed="64"/></patternFill></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="9">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="1" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="1" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="166" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="167" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="168" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="164" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyNumberFormat="1"/>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const splitSheets = (sheets) => sheets.flatMap((sheet) => {
  const columns = Array.isArray(sheet.columns) ? sheet.columns : [];
  const rows = Array.isArray(sheet.rows) ? sheet.rows : [];
  if (!columns.length) throw new Error(`La hoja "${sheet.name}" no tiene columnas`);
  if (columns.length > MAX_COLUMNS) throw new Error(`La hoja "${sheet.name}" excede ${MAX_COLUMNS} columnas`);
  const size = MAX_ROWS - 1;
  if (rows.length <= size) return [{ ...sheet, columns, rows }];
  const parts = [];
  for (let start = 0; start < rows.length; start += size) {
    const part = parts.length + 1;
    parts.push({ ...sheet, name: part === 1 ? sheet.name : `${sheet.name} (${part})`, columns, rows: rows.slice(start, start + size) });
  }
  return parts;
});

export async function buildXlsx(inputSheets) {
  if (!Array.isArray(inputSheets) || !inputSheets.length) throw new Error('Se requiere al menos una hoja');
  await new Promise((resolve) => {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(resolve, { timeout: 50 });
    else setTimeout(resolve, 0);
  });
  const usedNames = new Set();
  const sheets = splitSheets(inputSheets).map((sheet) => ({ ...sheet, name: sanitizeSheetName(sheet.name, usedNames) }));
  const sheetEntries = [];
  for (let index = 0; index < sheets.length; index += 1) {
    sheetEntries.push({
      name: `xl/worksheets/sheet${index + 1}.xml`,
      content: sheetXml(sheets[index]),
    });
    if (index > 0 && index % 4 === 0) {
      // Los XML grandes siguen siendo síncronos; ceder entre hojas mantiene viva la UI.
      // Si un único XML llegara a dominar, el siguiente paso es serializar filas en un Worker.
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }
  const workbookSheets = sheets.map((sheet, index) => (
    `<sheet name="${escapeXml(sheet.name)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`
  )).join('');
  const relationships = sheets.map((_, index) => (
    `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`
  )).join('');
  const entries = [
    {
      name: '[Content_Types].xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${sheets.map((_, index) => `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')}</Types>`,
    },
    {
      name: '_rels/.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    },
    {
      name: 'xl/workbook.xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${workbookSheets}</sheets></workbook>`,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${relationships}<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    },
    { name: 'xl/styles.xml', content: stylesXml },
    ...sheetEntries,
  ];
  return new Blob([zipArchive(await prepareZipEntries(entries))], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

export function downloadXlsx(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = sanitizeFileName(filename).replace(/\.xlsx$/i, '') + '.xlsx';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
